import { isGuid } from '../../classifier/util';
import { EventMeta } from '../../interfaces/meta.interface';

const getId = (log: any, key: string, type: number) => {
    if (log[key]) {
        const target = log[key].find((t: any) => t.Type === type);
        return target ? target.ID : undefined;
    }
};

const getModifiedProperties = (log: any, name: string): any => {
    if (log.ModifiedProperties) {
        const foundProperties = log.ModifiedProperties.find((p: any) => p.Name === name);
        switch (name) {
            case 'Role.DisplayName': {
                // A role record need not carry the property; throwing here fails the whole transform batch.
                const roleName: string | undefined = foundProperties?.NewValue;
                return roleName;
            }
            case 'Role.TemplateId': {
                // Microsoft's well-known template id of the role, for the privileged-role test (KRAK-470).
                const roleTemplateId: string | undefined = foundProperties?.NewValue;
                return roleTemplateId;
            }
            case 'AppRole.Value': {
                // The application permission an app-role assignment grants (`Mail.ReadWrite`, say).
                const permission: string | undefined = foundProperties?.NewValue;
                return permission;
            }
            case 'ConsentAction.Permissions': {
                if (foundProperties !== undefined) {
                    const consentActionPremissions: string = foundProperties.NewValue;
                    const str = consentActionPremissions.substring(
                        consentActionPremissions.lastIndexOf('[[') + 2,
                        consentActionPremissions.lastIndexOf(']') - 1,
                    );
                    if (str.includes('=>')) {
                        return undefined;
                    }
                    const strArray = str.split('], [');

                    const propsArray: {
                        ClientId: string;
                        ConsentType: string;
                        Id: string;
                        PrincipalId: string;
                        ResourceId: string;
                        Scope: string;
                    }[] = [];

                    strArray.forEach((s) => {
                        const properties = s.split(', ');
                        const obj: any = {};
                        properties.forEach((p) => {
                            const pair = p.split(': ');
                            obj[pair[0]] = pair[1];
                        });
                        propsArray.push(obj);
                    });
                    const result = propsArray.map((item) => {
                        return { ResourceId: item.ResourceId, Scope: item.Scope };
                    });
                    return result;
                } else {
                    return undefined;
                }
            }
        }
    }
};

type AppConsentDescFields = {
    /** Application Name */
    appName: string;
    /** Actor User ID */
    user: string;
};

const consentToApplication: EventMeta<any, AppConsentDescFields> = {
    title: 'Consent Granted For App',
    default: {
        description: 'Consent Granted For App',
        longDescription: 'A user granted consent to an Azure app',
        severity: 'info',
    },
    descriptionExtractor: (log: any) => {
        return {
            appName: getId(log, 'Target', 1),
            user: getId(log, 'Actor', 5),
            consentActionPremissions: getModifiedProperties(log, 'ConsentAction.Permissions') as
                | Array<{ ResourceId: string; Scope: string }>
                | string
                | undefined,
        };
    },
    fieldsExtractor: (log: any) => {
        return {
            ResultStatus: log.ResultStatus,
        };
    },
    classifications: [
        // consent to application.
        {
            severity: 'danger',
            description: (fields: any) =>
                `The application "${fields.appName}" was granted consent, which could expose data or permissions without admin review`,
            longDescription: (fields: any) =>
                `The application "${fields.appName}" was granted consent. Depending on its requested permissions, this may allow it to access organizational data or perform actions without explicit administrative approval. Review the app's permissions and origin to ensure it is safe and authorized.`,
            fields: {
                ResultStatus: ['Success'],
            },
        },
        {
            severity: 'warning',
            description: (fields: any) =>
                `The application "${fields.appName}" prompted for consent, but the grant failed`,
            longDescription:
                'Consent could have failed for a number of reasons, such as: the user lacks permissions, or a Conditional Access Policy blocked the request',
            fields: {
                ResultStatus: ['Failure'],
            },
        },
    ],
};

type AddMemberToRoleDescFields = {
    /** user upn */
    user: string;
    service_principal: string;
    /** The principal the role was granted to, by the best name the record carries: a UPN, else a display name, else an id. */
    target: string;
    /** The target's object id, lowercased, for the self-assignment test (KRAK-470). */
    targetObjectId: string;
    /** role name */
    roleName: string;
    /** Microsoft's well-known role template id. */
    roleTemplateId: string;
    /** The acting application's display name, set by the transform when an application performed the operation. */
    actorApp: string;
};

/**
 * The principal a role-membership record acted on, and the role. `Target` names a user by UPN (type 5); a service
 * principal by display name (type 1) and `ServicePrincipal_<objectId>` (type 2).
 *
 * @param log the raw record
 * @returns the description fields
 */
const roleMembershipFields = (log: any): AddMemberToRoleDescFields => {
    const user = getId(log, 'Target', 5);
    const service_principal = getId(log, 'Target', 2);
    const ids: string[] = Array.isArray(log.Target)
        ? log.Target.filter((t: any) => t.Type === 2).map((t: any) => String(t.ID))
        : [];
    const principalId =
        ids.map((id) => /^(?:ServicePrincipal|User)_(.+)$/i.exec(id)?.[1]).find((id): id is string => !!id) ??
        ids.find((id) => isGuid(id));

    return {
        user,
        service_principal,
        target: user ?? getId(log, 'Target', 1) ?? service_principal,
        targetObjectId: principalId?.toLowerCase(),
        roleName: getModifiedProperties(log, 'Role.DisplayName'),
        roleTemplateId: getModifiedProperties(log, 'Role.TemplateId'),
    } as AddMemberToRoleDescFields;
};

/** The acting application's name for a description, or a neutral fallback. */
const actorApp = (fields: Partial<AddMemberToRoleDescFields> | undefined): string =>
    fields?.actorApp ?? 'an application';

const addMemberToRole: EventMeta<any, AddMemberToRoleDescFields> = {
    title: 'User Added to Role Group',
    default: {
        description: 'User Added to Role Group',
        longDescription: 'A User was added to a Role Group.',
        severity: 'info',
    },
    descriptionExtractor: roleMembershipFields,
    classifications: [
        // An application granted itself the role (KRAK-470): the ConnectWise pattern. Critical regardless of IP.
        {
            severity: 'critical',
            description: (fields) => `The application "${actorApp(fields)}" granted itself "${fields.roleName}"`,
            longDescription: (fields) =>
                `The application "${actorApp(fields)}" added itself to "${fields.roleName}". No user signed in; the app acted on its own permissions. An application granting itself a privileged role is a known sign of a compromised vendor credential. Remove the role unless the app is meant to hold it, and check its credentials for additions you did not make.`,
            predicates: {
                isActorWorkloadIdentity: true,
                isSelfAssignment: true,
            },
        },
        // A Microsoft service (GDAP, PIM) manages role membership routinely.
        {
            severity: 'info',
            description: (fields) => `A Microsoft service added "${fields.target}" to "${fields.roleName}"`,
            longDescription: (fields) =>
                `A Microsoft service ("${fields.actorApp ?? 'a Microsoft application'}") added "${fields.target}" to "${fields.roleName}". This is routine for delegated administration and partner tooling.`,
            predicates: {
                isActorWorkloadIdentity: true,
                isMicrosoftFirstPartyApp: true,
            },
        },
        // An application nobody approved granted a privileged role.
        {
            severity: 'critical',
            description: (fields) =>
                `The application "${actorApp(fields)}" added "${fields.target}" to "${fields.roleName}"`,
            longDescription: (fields) =>
                `The application "${actorApp(fields)}" added "${fields.target}" to "${fields.roleName}". No user signed in, and the application is not one you have approved. Applications do not normally change role membership; remove the membership unless this app is meant to manage roles, and check the application's credentials for additions you did not make.`,
            predicates: {
                isActorWorkloadIdentity: true,
                isPrivilegedRole: true,
                isKnownVendorApp: false,
            },
        },
        // An approved application granted a privileged role from an address no rule allows.
        {
            severity: 'critical',
            description: (fields) =>
                `The application "${actorApp(fields)}" added "${fields.target}" to "${fields.roleName}" from an unapproved IP`,
            longDescription: (fields) =>
                `The application "${actorApp(fields)}" added "${fields.target}" to "${fields.roleName}" from an address not on your allowed list. No user signed in. Applications do not normally change role membership; confirm this app is expected to manage roles before leaving the membership in place.`,
            predicates: {
                isActorWorkloadIdentity: true,
                isPrivilegedRole: true,
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
            },
        },
        // An approved application granted a privileged role.
        {
            severity: 'danger',
            description: (fields) =>
                `The application "${actorApp(fields)}" added "${fields.target}" to "${fields.roleName}"`,
            longDescription: (fields) =>
                `The application "${actorApp(fields)}" added "${fields.target}" to "${fields.roleName}". No user signed in. Applications do not normally change role membership; confirm this app is expected to manage roles before leaving the membership in place.`,
            predicates: {
                isActorWorkloadIdentity: true,
                isPrivilegedRole: true,
            },
        },
        // An application granted a role outside the privileged list.
        {
            severity: 'warning',
            description: (fields) =>
                `The application "${actorApp(fields)}" added "${fields.target}" to "${fields.roleName}"`,
            longDescription: (fields) =>
                `The application "${actorApp(fields)}" added "${fields.target}" to "${fields.roleName}". No user signed in. Applications do not normally change role membership; confirm this app is expected to manage roles before leaving the membership in place.`,
            predicates: {
                isActorWorkloadIdentity: true,
            },
        },
        // add member to role: fraudulent IP. not in allowed list.
        {
            severity: 'critical',
            description: (fields) => {
                if (fields.user) {
                    return `A user "${fields.user}" was added to "${fields.roleName}" from a suspicious IP`;
                } else {
                    return `A service principal "${fields.service_principal}" was added to "${fields.roleName}" from a suspicious IP`;
                }
            },
            longDescription: (fields) => {
                if (fields.user) {
                    return `A user "${fields.user}" was added to the role group "${fields.roleName}". The action originated from a suspicious IP that is not on your allowed list. Adding users to privileged groups can enable unauthorized access or administrative control. Because this came from a suspicious IP, review immediately for potential privilege escalation.`;
                } else {
                    return `A service principal "${fields.service_principal}" was added to the role group "${fields.roleName}". The action originated from a suspicious IP that is not on your allowed list. Granting role group membership to a service principal may give an application elevated permissions. Because the source was suspicious, treat this as high risk and investigate immediately.`;
                }
            },
            predicates: {
                isFraudulentIP: true,
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
            },
        },
        // add member to role: not in allowed list.
        {
            severity: 'danger',
            description: (fields) => {
                if (fields.user) {
                    return `A user "${fields.user}" was added to "${fields.roleName}" from an unapproved IP`;
                } else {
                    return `A service principal "${fields.service_principal}" was added to "${fields.roleName}" from an unapproved IP`;
                }
            },
            longDescription: (fields) => {
                if (fields.user) {
                    return `A user "${fields.user}" was added to the role group "${fields.roleName}". The action originated from an unapproved IP that is not on your allowed list. Adding users to privileged groups can enable unauthorized access or administrative control. Because this came from an unapproved IP, review immediately for potential privilege escalation.`;
                } else {
                    return `A service principal "${fields.service_principal}" was added to the role group "${fields.roleName}". The action originated from an unapproved IP that is not on your allowed list. Granting role group membership to a service principal may give an application elevated permissions. Because the source was suspicious, treat this as high risk and investigate immediately.`;
                }
            },
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
            },
        },
        // add member to role: fraud IP.
        {
            severity: 'warning',
            description: (fields) => {
                if (fields.user) {
                    return `A user "${fields.user}" was added to "${fields.roleName}" from a suspicious IP`;
                } else {
                    return `A service principal "${fields.service_principal}" was added to "${fields.roleName}" from a suspicious IP`;
                }
            },
            longDescription: (fields) => {
                if (fields.user) {
                    return `A user "${fields.user}" was added to "${fields.roleName}" from a suspicious IP.`;
                } else {
                    return `A service principal "${fields.service_principal}" was added to "${fields.roleName}" from a suspicious IP.`;
                }
            },
            predicates: {
                isFraudulentIP: true,
            },
        },
        // add member to role.
        {
            severity: 'warning',
            description: (fields) => {
                if (fields.user) {
                    return `A user "${fields.user}" was added to "${fields.roleName}"`;
                } else {
                    return `A service principal "${fields.service_principal}" was added to "${fields.roleName}"`;
                }
            },
            longDescription: (fields) => {
                if (fields.user) {
                    return `A user "${fields.user}" was added to "${fields.roleName}".`;
                } else {
                    return `A service principal "${fields.service_principal}" was added to "${fields.roleName}".`;
                }
            },
        },
    ],
};

const removeMemberFromRole: EventMeta<any, AddMemberToRoleDescFields> = {
    requiresWorkloadIdentities: true,
    title: 'Member Removed from Role Group',
    default: {
        description: 'Member Removed from Role Group',
        longDescription: 'A member was removed from a Role Group.',
        severity: 'info',
    },
    descriptionExtractor: roleMembershipFields,
    classifications: [
        // An application other than a Microsoft service removed a role member (KRAK-470).
        {
            severity: 'warning',
            description: (fields) =>
                `The application "${actorApp(fields)}" removed "${fields.target}" from "${fields.roleName}"`,
            longDescription: (fields) =>
                `The application "${actorApp(fields)}" removed "${fields.target}" from "${fields.roleName}". No user signed in. An application removing an administrator is usually clean-up, but it is also how an intruder locks out a defender; confirm the removal was expected.`,
            predicates: {
                isActorWorkloadIdentity: true,
                isMicrosoftFirstPartyApp: false,
            },
        },
    ],
};

type ServicePrincipalCredentialsDescFields = {
    appName: string;
    user: string;
    actorApp: string;
};

/** The application a credential or owner operation acted on, and the person who did it (absent for an app actor). */
const credentialTargetFields = (log: any): ServicePrincipalCredentialsDescFields => {
    return {
        appName: getId(log, 'Target', 1),
        user: getId(log, 'Actor', 5),
    } as ServicePrincipalCredentialsDescFields;
};

const addServicePrincipalCredentials: EventMeta<any, ServicePrincipalCredentialsDescFields> = {
    requiresWorkloadIdentities: true,
    title: 'Service Principal Credentials Added',
    default: {
        description: 'A credential was added to a service principal',
        longDescription: 'A client secret or certificate was added to an Entra ID service principal.',
        severity: 'info',
    },
    descriptionExtractor: credentialTargetFields,
    classifications: [
        // A Microsoft service manages the credentials of the principals it owns.
        {
            severity: 'warning',
            description: (fields: any) =>
                `A credential was added to "${fields?.appName ?? 'an application'}" by the Microsoft service "${fields?.actorApp ?? 'a Microsoft application'}"`,
            longDescription: (fields: any) =>
                `A client secret or certificate was added to "${fields?.appName ?? 'an application'}" by the Microsoft service "${fields?.actorApp ?? 'a Microsoft application'}". Microsoft services rotate the credentials of the principals they manage; review only if unexpected.`,
            predicates: {
                isActorWorkloadIdentity: true,
                isMicrosoftFirstPartyApp: true,
            },
        },
        // An application planted a credential on another principal (KRAK-470): the backdoor pattern.
        {
            severity: 'critical',
            description: (fields: any) =>
                `A credential was added to "${fields?.appName ?? 'an application'}" by the application "${fields?.actorApp ?? 'another application'}"`,
            longDescription: (fields: any) =>
                `A client secret or certificate was added to "${fields?.appName ?? 'an application'}" by the application "${fields?.actorApp ?? 'another application'}", not by a person. Whoever holds the new secret can sign in as that app without a user, MFA or Conditional Access (MITRE T1098.001). Remove the credential unless you added it, and review what the acting application is permitted to do.`,
            predicates: {
                isActorWorkloadIdentity: true,
            },
        },
        {
            severity: 'danger',
            description: (fields: any) =>
                `A credential was added to "${fields?.appName ?? 'an application'}" from an unapproved IP`,
            longDescription: (fields: any) =>
                `A client secret or certificate was added to "${fields?.appName ?? 'an application'}" by ${fields?.user ?? 'an admin'}. The action originated from a location not on your approved list. Adding credentials to an app can enable persistent access and should be reviewed (MITRE T1098.001).`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
            },
        },
        {
            severity: 'warning',
            description: (fields: any) => `A credential was added to "${fields?.appName ?? 'an application'}"`,
            longDescription: (fields: any) =>
                `A client secret or certificate was added to "${fields?.appName ?? 'an application'}" by ${fields?.user ?? 'an admin'}. Adding credentials to an app can enable persistent access and should be reviewed (MITRE T1098.001).`,
        },
    ],
};

const removeServicePrincipalCredentials: EventMeta<any, ServicePrincipalCredentialsDescFields> = {
    requiresWorkloadIdentities: true,
    title: 'Service Principal Credentials Removed',
    default: {
        description: 'A credential was removed from a service principal',
        longDescription: 'A client secret or certificate was removed from an Entra ID service principal.',
        severity: 'info',
    },
    descriptionExtractor: credentialTargetFields,
    classifications: [
        {
            severity: 'info',
            description: (fields: any) => `A credential was removed from "${fields?.appName ?? 'an application'}"`,
            longDescription: (fields: any) =>
                `A client secret or certificate was removed from "${fields?.appName ?? 'an application'}" by ${fields?.user ?? fields?.actorApp ?? 'an admin'}. Usually clean-up; review only if unexpected.`,
        },
    ],
};

type OwnerDescFields = ServicePrincipalCredentialsDescFields & {
    /** The principal made an owner, by UPN. */
    owner: string;
};

const addOwnerToApplication: EventMeta<any, OwnerDescFields> = {
    requiresWorkloadIdentities: true,
    title: 'Application Owner Added',
    default: {
        description: 'An owner was added to an application',
        longDescription: 'A principal was made an owner of an Entra ID application registration or service principal.',
        severity: 'info',
    },
    descriptionExtractor: (log: any) => {
        return {
            ...credentialTargetFields(log),
            owner: getId(log, 'Target', 5),
        };
    },
    classifications: [
        // An application other than a Microsoft service made someone an owner (KRAK-470): an owner can add credentials.
        {
            severity: 'critical',
            description: (fields: any) =>
                `The application "${fields?.actorApp ?? 'an application'}" made "${fields?.owner ?? 'a principal'}" an owner of "${fields?.appName ?? 'an application'}"`,
            longDescription: (fields: any) =>
                `The application "${fields?.actorApp ?? 'an application'}" made "${fields?.owner ?? 'a principal'}" an owner of "${fields?.appName ?? 'an application'}". No user signed in. An owner can add credentials to the application and sign in as it, so this is a persistence route (MITRE T1098.001). Remove the owner unless the acting application is meant to manage this one, and check the application's credentials.`,
            predicates: {
                isActorWorkloadIdentity: true,
                isMicrosoftFirstPartyApp: false,
            },
        },
        {
            severity: 'warning',
            description: (fields: any) =>
                `"${fields?.owner ?? 'A principal'}" was made an owner of "${fields?.appName ?? 'an application'}"`,
            longDescription: (fields: any) =>
                `${fields?.user ?? 'An admin'} made "${fields?.owner ?? 'a principal'}" an owner of "${fields?.appName ?? 'an application'}". An owner can add credentials to the application and sign in as it; confirm the owner is expected.`,
        },
    ],
};

const addOwnerToServicePrincipal: EventMeta<any, OwnerDescFields> = {
    ...addOwnerToApplication,
    requiresWorkloadIdentities: true,
    title: 'Service Principal Owner Added',
};

type AppRoleAssignmentDescFields = ServicePrincipalCredentialsDescFields & {
    /** The application permission granted (`Mail.ReadWrite`, say). */
    permission: string;
};

const addAppRoleAssignmentToServicePrincipal: EventMeta<any, AppRoleAssignmentDescFields> = {
    requiresWorkloadIdentities: true,
    title: 'App Role Assigned to Service Principal',
    default: {
        description: 'An application permission was granted to a service principal',
        longDescription: 'A service principal was granted an application permission (app role).',
        severity: 'info',
    },
    descriptionExtractor: (log: any) => {
        return {
            ...credentialTargetFields(log),
            permission: getModifiedProperties(log, 'AppRole.Value'),
        };
    },
    classifications: [
        // An application other than a Microsoft service granted an application permission (KRAK-470).
        {
            severity: 'warning',
            description: (fields: any) =>
                `The application "${fields?.actorApp ?? 'an application'}" granted "${fields?.appName ?? 'an application'}" the permission "${fields?.permission ?? 'an application permission'}"`,
            longDescription: (fields: any) =>
                `The application "${fields?.actorApp ?? 'an application'}" granted "${fields?.appName ?? 'an application'}" the application permission "${fields?.permission ?? 'an application permission'}". No user signed in. Application permissions let an app read or change data with no user present; confirm the grant was expected.`,
            predicates: {
                isActorWorkloadIdentity: true,
                isMicrosoftFirstPartyApp: false,
            },
        },
    ],
};

const addDelegatedPermissionGrant: EventMeta<any, ServicePrincipalCredentialsDescFields> = {
    requiresWorkloadIdentities: true,
    title: 'Delegated Permission Granted',
    default: {
        description: 'A delegated permission was granted to an application',
        longDescription: 'An application was granted delegated permissions.',
        severity: 'info',
    },
    descriptionExtractor: credentialTargetFields,
    classifications: [
        // An application other than a Microsoft service granted delegated permissions (KRAK-470).
        {
            severity: 'warning',
            description: (fields: any) =>
                `The application "${fields?.actorApp ?? 'an application'}" granted "${fields?.appName ?? 'an application'}" delegated permissions`,
            longDescription: (fields: any) =>
                `The application "${fields?.actorApp ?? 'an application'}" granted "${fields?.appName ?? 'an application'}" delegated permissions. No user signed in. Review the permissions and the application that granted them.`,
            predicates: {
                isActorWorkloadIdentity: true,
                isMicrosoftFirstPartyApp: false,
            },
        },
    ],
};

const addUser: EventMeta<any> = {
    title: 'New User Created',
    default: {
        description: 'New User Created',
        longDescription: 'A new user was created on your tenant.',
        severity: 'info',
    },
};

const changeUserLicense: EventMeta<any> = {
    title: 'User License Changed',
    default: {
        description: 'User License Changed',
        longDescription: 'A User License has changed.',
        severity: 'info',
    },
    descriptionExtractor: (log: any) => {
        return {
            user: getId(log, 'Target', 5),
        };
    },
    classifications: [
        {
            severity: 'warning',
            description: (fields: any) =>
                `The license for ${fields.user} was updated, which could modify the services, permissions, or capabilities assigned to the user`,
            longDescription: (fields: any) =>
                `The license for user ${fields.user} was changed. Changes to a user's license can impact which services and features they can access. Review the update to ensure it aligns with intended permissions and organizational policies`,
        },
    ],
};

type UpdateAppDescFields = {
    appName: string;
    user: string;
};

const updateAppCertificatesAndSecrets: EventMeta<any, UpdateAppDescFields> = {
    title: 'App Credentials Modified',
    default: {
        description: 'Application certificates or secrets were updated',
        longDescription: "An application's certificates or client secrets were added, updated, or removed.",
        severity: 'info',
    },
    descriptionExtractor: (log: any) => {
        return {
            appName: getId(log, 'Target', 1),
            user: getId(log, 'Actor', 5),
        };
    },
    classifications: [
        // An application, not a person, changed the credentials (KRAK-470).
        {
            severity: 'critical',
            description: (fields: any) =>
                `App credentials modified for "${fields?.appName ?? 'an application'}" by the application "${fields?.actorApp ?? 'another application'}"`,
            longDescription: (fields: any) =>
                `A client secret or certificate was added to "${fields?.appName ?? 'an application'}" by the application "${fields?.actorApp ?? 'another application'}", not by a person. Whoever holds the new secret can sign in as that app without a user, MFA or Conditional Access (MITRE T1098.001). Remove the credential unless you added it, and review what the acting application is permitted to do.`,
            predicates: {
                isActorWorkloadIdentity: true,
                isMicrosoftFirstPartyApp: false,
            },
        },
        {
            severity: 'danger',
            description: (fields: any) =>
                `App credentials modified for "${fields?.appName ?? 'an application'}" from an unapproved location`,
            longDescription: (fields: any) =>
                `Client secrets or certificates were added or modified for the application "${fields?.appName ?? 'an application'}" by ${fields?.user ?? 'an admin'}. The action originated from a location not on your approved list. Adding credentials to an app registration is a known persistence technique (MITRE T1098.001) that can allow unauthorized access to your environment.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
            },
        },
        {
            severity: 'warning',
            description: (fields: any) => `App credentials modified for "${fields?.appName ?? 'an application'}"`,
            longDescription: (fields: any) =>
                `Client secrets or certificates were added or modified for the application "${fields?.appName ?? 'an application'}" by ${fields?.user ?? 'an admin'}. Adding credentials to an app registration can enable persistent access and should be reviewed (MITRE T1098.001).`,
        },
    ],
};

const updateApplication: EventMeta<any, UpdateAppDescFields> = {
    title: 'Application Updated',
    default: {
        description: 'An application registration was updated',
        longDescription: 'An Entra ID application registration was modified.',
        severity: 'info',
    },
    descriptionExtractor: (log: any) => {
        return {
            appName: getId(log, 'Target', 1),
            user: getId(log, 'Actor', 5),
        };
    },
    classifications: [
        {
            severity: 'warning',
            description: (fields: any) => `Application "${fields?.appName ?? 'an application'}" was updated`,
            longDescription: (fields: any) =>
                `The application registration "${fields?.appName ?? 'an application'}" was modified by ${fields?.user ?? 'an admin'}. Changes to application registrations can affect permissions and access patterns.`,
        },
    ],
};

const updateServicePrincipal: EventMeta<any, UpdateAppDescFields> = {
    title: 'Service Principal Updated',
    default: {
        description: 'A service principal was updated',
        longDescription: 'An Entra ID service principal was modified.',
        severity: 'info',
    },
    descriptionExtractor: (log: any) => {
        return {
            appName: getId(log, 'Target', 1),
            user: getId(log, 'Actor', 5),
        };
    },
    classifications: [
        {
            severity: 'warning',
            description: (fields: any) => `Service principal "${fields?.appName ?? 'an application'}" was updated`,
            longDescription: (fields: any) =>
                `The service principal "${fields?.appName ?? 'an application'}" was modified by ${fields?.user ?? 'an admin'}. Changes to service principals can affect application permissions and authentication.`,
        },
    ],
};

const createAppCertificatesAndSecrets: EventMeta<any, UpdateAppDescFields> = {
    title: 'App Credentials Created',
    default: {
        description: 'Application certificates or secrets were created',
        longDescription: "An application's certificates or client secrets were created.",
        severity: 'info',
    },
    descriptionExtractor: (log: any) => {
        return {
            appName: getId(log, 'Target', 1),
            user: getId(log, 'Actor', 5),
        };
    },
    classifications: [
        // An application, not a person, changed the credentials (KRAK-470).
        {
            severity: 'critical',
            description: (fields: any) =>
                `App credentials created for "${fields?.appName ?? 'an application'}" by the application "${fields?.actorApp ?? 'another application'}"`,
            longDescription: (fields: any) =>
                `A client secret or certificate was added to "${fields?.appName ?? 'an application'}" by the application "${fields?.actorApp ?? 'another application'}", not by a person. Whoever holds the new secret can sign in as that app without a user, MFA or Conditional Access (MITRE T1098.001). Remove the credential unless you added it, and review what the acting application is permitted to do.`,
            predicates: {
                isActorWorkloadIdentity: true,
                isMicrosoftFirstPartyApp: false,
            },
        },
        {
            severity: 'danger',
            description: (fields: any) =>
                `App credentials created for "${fields?.appName ?? 'an application'}" from an unapproved location`,
            longDescription: (fields: any) =>
                `Client secrets or certificates were created for the application "${fields?.appName ?? 'an application'}" by ${fields?.user ?? 'an admin'}. The action originated from a location not on your approved list. Adding credentials to an app registration is a known persistence technique (MITRE T1098.001) that can allow unauthorized access to your environment.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
            },
        },
        {
            severity: 'warning',
            description: (fields: any) => `App credentials created for "${fields?.appName ?? 'an application'}"`,
            longDescription: (fields: any) =>
                `Client secrets or certificates were created for the application "${fields?.appName ?? 'an application'}" by ${fields?.user ?? 'an admin'}. Adding credentials to an app registration can enable persistent access and should be reviewed (MITRE T1098.001).`,
        },
    ],
};

type DevicePersistenceDescFields = {
    /** Device (or other target object) display name, from the event Target. */
    device: string;
    /** UPN of the actor that performed the operation. */
    user: string;
};

/**
 * Build an {@link EventMeta} for an operation an attacker uses to keep access after stealing a
 * token: registering a device, binding an account to one, or adding an authentication method.
 *
 * These operations are also entirely routine during normal enrolment, so they deliberately carry
 * NO catch-all classification — an ordinary occurrence falls through to the `info` default and
 * raises nothing. Only one originating outside every configured allow-list, and not from
 * Microsoft's own service ranges, grades `danger`. An application acting as itself reaches only
 * branches that opt in with `isActorWorkloadIdentity` (KRAK-470); these have none, so it still falls
 * through to `info`.
 *
 * @param title - Alert title for this operation.
 * @param summary - Plain description of the operation, used for the `info` default.
 * @param dangerDescription - Alert description when the operation came from an unapproved location.
 * @param risk - Why the operation matters once it originates from an unapproved location.
 * @returns The {@link EventMeta} for the operation.
 */
function devicePersistenceMeta(
    title: string,
    summary: string,
    dangerDescription: string,
    risk: string,
): EventMeta<any, DevicePersistenceDescFields> {
    return {
        title,
        default: {
            description: summary,
            longDescription: summary,
            severity: 'info',
        },
        descriptionExtractor: (log: any) => {
            return {
                device: getId(log, 'Target', 1),
                user: getId(log, 'Actor', 5),
            };
        },
        classifications: [
            {
                severity: 'danger',
                description: dangerDescription,
                longDescription: (fields: any) =>
                    `${summary} by ${fields?.user ?? 'a user'}${fields?.device ? ` ("${fields.device}")` : ''}. The action originated from a location that is not on your allowed list. ${risk}`,
                predicates: {
                    isAllowedASN: false,
                    isAllowedCountry: false,
                    isAllowedRegion: false,
                    isAllowedIP: false,
                    // Microsoft's own service ranges perform these operations routinely.
                    isMicrosoftActivity: false,
                },
            },
        ],
    };
}

const addDevice = devicePersistenceMeta(
    'Device Registered',
    'A device was registered in the directory',
    'A device was registered from an unapproved location',
    'Registering an attacker-controlled device is a common way to retain access after a token has been stolen (MITRE T1098.005).',
);

const addRegisteredOwnerToDevice = devicePersistenceMeta(
    'Device Owner Added',
    'A registered owner was added to a device',
    'A device owner was added from an unapproved location',
    'Binding an account to a device can give an attacker a durable, trusted foothold (MITRE T1098.005).',
);

const addRegisteredUsersToDevice = devicePersistenceMeta(
    'Device User Added',
    'A registered user was added to a device',
    'A device user was added from an unapproved location',
    'Binding an account to a device can give an attacker a durable, trusted foothold (MITRE T1098.005).',
);

const updateDevice = devicePersistenceMeta(
    'Device Updated',
    'A device registration was updated',
    'A device registration was updated from an unapproved location',
    'Changing device attributes can be used to make an attacker-controlled device appear trusted.',
);

const enableStrongAuthentication = devicePersistenceMeta(
    'Authentication Method Enabled',
    'Strong authentication was enabled for an account',
    'An authentication method was enabled from an unapproved location',
    'Adding an authentication method the attacker controls lets them satisfy MFA on later sign-ins (MITRE T1556.006).',
);

const operations = {
    'Consent to application.': consentToApplication,
    'Add member to role.': addMemberToRole,
    // Workload-identity operations (KRAK-470): what a compromised vendor application does with its access. Each is
    // `requiresWorkloadIdentities`: undefined for a classifier without the application inventory, so the legacy app
    // and an MSP on v1 see them exactly as before the ticket.
    'Remove member from role.': removeMemberFromRole,
    'Add service principal credentials.': addServicePrincipalCredentials,
    'Remove service principal credentials.': removeServicePrincipalCredentials,
    'Add owner to application.': addOwnerToApplication,
    'Add owner to service principal.': addOwnerToServicePrincipal,
    'Add app role assignment to service principal.': addAppRoleAssignmentToServicePrincipal,
    'Add delegated permission grant.': addDelegatedPermissionGrant,
    'Add user.': addUser,
    'Change user license.': changeUserLicense,
    'Update application – Certificates and secrets management': updateAppCertificatesAndSecrets,
    'Create application – Certificates and secrets management': createAppCertificatesAndSecrets,
    'Update application.': updateApplication,
    'Update service principal.': updateServicePrincipal,
    // Post-compromise persistence (OS-437 point 2): device registration + authentication-method
    // changes. Caught by the geo/allow-list predicates the classifier already has, on events the
    // UAL pipeline already ingests — no new data source.
    'Add device.': addDevice,
    'Add registered owner to device.': addRegisteredOwnerToDevice,
    'Add registered users to device.': addRegisteredUsersToDevice,
    'Update device.': updateDevice,
    'Enable Strong Authentication.': enableStrongAuthentication,
};

export default operations;
